import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { catchError, filter, finalize, of, Subject, switchMap, tap } from 'rxjs';
import { UpdateBlockListFormService } from '../../../services/update-block-list-form.service';
import { CommonModule } from '@angular/common';
import { FullPageMaskService } from 'common';
import { BannedWordsApiService } from '../../../services/banned-words-api.service';
import { DialogService } from '../../../services/dialog.service';
import { MatIconModule } from '@angular/material/icon';

export enum UpdateBlockListDialogMode {
  Add,
  Edit,
}

export interface UpdateBlockListDialogData {
  mode: UpdateBlockListDialogMode;
  id?: number;
  word?: string;
}

@Component({
  selector: 'si-update-block-list-dialog',
  standalone: true,
  imports: [MatDialogModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule, MatButtonModule, CommonModule, MatIconModule],
  templateUrl: './update-block-list-dialog.component.html',
  styleUrl: './update-block-list-dialog.component.scss'
})
export class UpdateBlockListDialogComponent implements OnInit, OnDestroy {
  dialogRef = inject(MatDialogRef<UpdateBlockListDialogComponent>);
  data = inject<UpdateBlockListDialogData>(MAT_DIALOG_DATA);
  dialogService = inject(DialogService);
  bannedWordsApiService = inject(BannedWordsApiService);
  formService = inject(UpdateBlockListFormService);
  maskService = inject(FullPageMaskService);

  private _destroy$ = new Subject<void>();

  wordControl = new FormControl('');

  get form() {
    return this.formService.form;
  }

  get addNewWordMode() {
    return this.data.mode === UpdateBlockListDialogMode.Add;
  }

  get word() {
    return this.form.value.word;
  }

  get errorMessage() {
    return this.form.controls.word.getError('response') ?? 'Please provide a word';
  }

  ngOnInit() {
    this.formService.init(this.data.word);
  }

  handleSubmit() {
    if (this.form.invalid) {
      return;
    }

    this.dialogService.openConfirmation().afterClosed().pipe(
      filter(confirmed => confirmed),
      tap(() => this.maskService.showLoading()),
      switchMap(() => {
        if (this.addNewWordMode) {
          return this.bannedWordsApiService.addBannedWord$(this.word)
        } else {
          return this.bannedWordsApiService.updateBannedWord$(this.data.id, this.word);
        }
      }),
      catchError((error) => {
        this.form.controls.word.setErrors({ response: error.error ?? error.message }, { emitEvent: true });
        return of(error);
      }),
      finalize(() => this.maskService.hideLoading()),
    ).subscribe((error) => {
      if (!error) this.dialogRef.close(this.word);
    });
  }

  ngOnDestroy() {
    this._destroy$.next();
  }
}
